import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Autocomplete, BodyBig, Box, EditIcon, Grid, IconButton, OptionType, Skeleton, Snackbar, TextField } from '@parspec/pixel';
import { updateBomDataSelectionStatus, updateOmDataSelectionStatus, useGetBodDetailQuery, useGetDatasheetDetailQuery, useGetOmDetailQuery, useUpdateBodDMutation } from '../../BOM/queries';
import RefreshBodModal from './RefreshBodModal';
import { someThingWentWrongMsg, updateMsg } from '../constants';
import { useParspecNavigation } from 'src/app/Context';
import { invalidateGetSelectedDocument } from '../../BOM/Datasheets/AnnotateDatasheet/queries';
import { useCompanyMfgQuery } from 'src/features/Settings/Manufacturer/queries';
import { ManufacturerDropdownType } from 'src/features/BOM/productFinder/setRequirements/types';
import { searchManufacturer } from '../utils/utils';
import { getFormattedManufacturerName } from 'src/features/BOM/shared/utils';
import { isValidCharactersForMfg } from 'src/features/Settings/Manufacturer/utils';
import { INVALID_CHARACTERS_IN_MFG_NAME } from 'src/features/Settings/Manufacturer/constants';

interface ProductInfoProps {
    onAccept?: (val?: boolean) => void;
    isAlternates?: boolean;
    isAnnotationPage?: boolean;
}

interface Manufacturer {
    manufacturer_name: string;
}
const ProductInfo = (props: ProductInfoProps) => {
    const { onAccept, isAlternates, isAnnotationPage } = props;
    const { hasUnsavedChangesRef } = useParspecNavigation();
    const [editDetails, setEditDetails] = useState(false);
    const [mfgValue, setMfgValue] = useState('')
    const [productData, setProductData] = useState({
        category: '',
        manufacturer: '',
        model_number: ''
    });
    const [refreshBodDetail, setOpenRefreshBodDetail] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const { bomId, productId, documentType, subDocumentType } = useParams();
    const manufacturerRef = useRef<HTMLInputElement>();
    const modelRef = useRef<HTMLInputElement>();
    const typeDivRef = useRef<HTMLDivElement>(null);
    const modelDivRef = useRef<HTMLDivElement>(null);
    const { data: companyMfgData, isLoading: isMfgListLoading } = useCompanyMfgQuery();

    const { data: bodData, isLoading } = useGetBodDetailQuery(bomId!, productId!, {
        enabled: Boolean(bomId) && Boolean(productId)
    });

    const { refetch: fetchDatasheetInfo } = useGetDatasheetDetailQuery(bomId!, productId!, {
        enabled: false
    });

    const { refetch: fetchOmInfo } = useGetOmDetailQuery(bomId!, productId!, {
        enabled: false
    });

    useEffect(() => {
        if (bodData?.data) {
            const { manufacturer, model_number, category } = bodData.data;
            setMfgValue(manufacturer)
            setProductData({ manufacturer, model_number, category });
        }
    }, [bodData]);

    const { mutateAsync: updateData } = useUpdateBodDMutation();

    //to toggle the refresh data dialog
    const toggleRefreshDataSheet = () => setOpenRefreshBodDetail((old) => !old);

    const onClickRefreshOption = () => {
        if (onAccept) onAccept(true);
        hasUnsavedChangesRef.current = false;
        if (subDocumentType === 'datasheet') {
            invalidateGetSelectedDocument(bomId!, productId!, documentType!, subDocumentType);
            updateBomDataSelectionStatus({ bomId: bomId!, bodId: productId!, selectionStatus: 'NOT_CONFIRMED', annotationStatus: 'NOT_CONFIRMED' });
        } else {
            updateOmDataSelectionStatus({ bomId: bomId!, bodId: productId!, selectionStatus: 'NOT_CONFIRMED', subDocumentType, annotationStatus: 'NOT_CONFIRMED' });
        }
        toggleRefreshDataSheet();
    };

    // to change the Product info details
    const handleMfgChange = (e: React.SyntheticEvent<Element, Event>) => {
        const target = e.target as HTMLInputElement;
        const selectedValue = target.value as unknown as Manufacturer;
        if (!selectedValue || !selectedValue.manufacturer_name) return;

        setProductData((old) => ({ ...old, manufacturer: selectedValue.manufacturer_name || '' }));
    };

    const handleInputChange = (event: React.SyntheticEvent, newInputValue: string) => {
        const value = getFormattedManufacturerName(companyMfgData, newInputValue as string)

        setProductData((old) => ({
            ...old,
            manufacturer: newInputValue ? value : '', // Set the appropriate value
        }));
        setMfgValue(newInputValue);
    };
    const handleChange = (e: any) => {
        const { name, value } = e.target;
        if (name) {
            setProductData((old) => ({ ...old, [name]: value }));
        }
    };

    const handleEditProductInfo = async () => {
        if (!editDetails) setEditDetails(true);
        else {
            const { manufacturer, model_number, category } = bodData?.data || {};
            const { isValid, sanitizedValue } = isValidCharactersForMfg(productData.manufacturer || '')
            const manufacturerValue = isValid ? (productData.manufacturer || '').trim() : sanitizedValue;
            if (productData.category !== category || productData.manufacturer !== manufacturer || productData.model_number !== model_number) {

                try {
                    await updateData({
                        bomId: bomId!,
                        input: { id: bodData?.data?.id, ...productData, manufacturer: manufacturerValue }
                    });
                    if (!isAlternates) {
                        let detailResponse;
                        if (subDocumentType === 'datasheet') {
                            detailResponse = await fetchDatasheetInfo();
                        } else {
                            detailResponse = await fetchOmInfo();
                        }

                        if (!Object.keys(detailResponse?.data?.data?.[`selected_${subDocumentType}`] || {}).length) {
                            hasUnsavedChangesRef.current = false;
                            if (onAccept) onAccept(false);
                        } else if (!isAnnotationPage) toggleRefreshDataSheet();
                    }
                    setSnackbarMessage(updateMsg);
                } catch (e: any) {
                    setSnackbarMessage(someThingWentWrongMsg);
                }
            }
            setMfgValue(manufacturerValue)
            setEditDetails(false);
        }
    };

    const onPressEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const target = e.target as HTMLInputElement;
        if (e.key === 'Enter' && target.name === 'model_number') {
            modelRef?.current?.blur();
            return handleEditProductInfo();
        }
        return;
    };


    const handleOutsideClick = (e: globalThis.MouseEvent) => {
        if (editDetails) {
            const clickedElement = e.target as HTMLElement;
            if (modelDivRef?.current?.contains(clickedElement as Node) ||
                typeDivRef?.current?.contains(clickedElement as Node) ||
                clickedElement.classList.contains('MuiAutocomplete-option') ||
                clickedElement.classList.contains('MuiSvgIcon-root')
                || clickedElement.classList.contains('MuiAutocomplete-listbox') ||

                clickedElement.id === 'mfgName' ||
                clickedElement.tagName === 'path'
            ) return;
            handleEditProductInfo();
        }
    };
    const handleOutsideKeyClick = (e: any) => {
        if (e.key === 'Enter') {
            if (e.target.name === "category") {
                const mfgNameElement = document.getElementById('mfgName') as HTMLElement;
                if (mfgNameElement) {
                    return mfgNameElement.focus();
                }
            }
            if (e.target?.id === 'mfgName') {
                return modelRef?.current?.focus();
            }
            return;
        }
    }
    useEffect(() => {
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleOutsideKeyClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.addEventListener('keydown', handleOutsideKeyClick);


        };
    }, [editDetails, productData]);


    const filterHandler = (options: OptionType<ManufacturerDropdownType>[] | { [index: string]: string | number; }[], params: { inputValue: string }) => {
        return searchManufacturer(options as OptionType<ManufacturerDropdownType>[], params.inputValue.trim());
    };

    const handleMfgBlur = () => {
        const { isValid, sanitizedValue } = isValidCharactersForMfg(productData.manufacturer || '');
        if (!isValid) {
            setMfgValue(() => sanitizedValue)
            setProductData((old) => ({ ...old, manufacturer: sanitizedValue }));
            setSnackbarMessage(INVALID_CHARACTERS_IN_MFG_NAME);
        }
    }
    return (
        <>
            <Snackbar open={Boolean(snackbarMessage)} onClose={() => setSnackbarMessage('')} message={snackbarMessage} />
            {isLoading && (
                <Grid container spacing={3}>
                    <Grid xs={2} item md={2} lg={2}>
                        <Skeleton width="100%" height="36px" />
                    </Grid>
                    <Grid xs={4} item md={4} lg={4}>
                        <Skeleton width="100%" height="36px" />
                    </Grid>
                    <Grid xs={6} item md={6} lg={6}>
                        <Skeleton width="100%" height="36px" />
                    </Grid>
                </Grid>
            )}
            {bodData && (
                <>
                    {!editDetails ? (
                        <Grid container>
                            <Grid item xs={12} md={12} lg={12}>
                                <Box display="flex" gap={4}>
                                    <Box maxWidth="20%">
                                        <BodyBig color={!productData.category ? 'text.secondary' : ''}>{productData.category || 'Enter type here'}</BodyBig>
                                    </Box>

                                    <Box maxWidth="40%">
                                        <BodyBig color={!productData.manufacturer ? 'text.secondary' : ''}>{productData.manufacturer.toUpperCase() || 'Enter manufacturer here'}</BodyBig>
                                    </Box>

                                    <Box flex={1} display="flex" gap={4} alignItems="center">
                                        <BodyBig color={!productData.model_number ? 'text.secondary' : ''}>{productData.model_number.toUpperCase() || 'Enter model number here'}</BodyBig>
                                        <Box>
                                            <IconButton size="small" onClick={handleEditProductInfo}>
                                                <EditIcon color={editDetails ? 'primary' : 'secondary'} fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid container spacing={3}>
                            <Grid xs={2} item md={2} lg={2}>
                                <TextField ref={typeDivRef} value={productData.category || ''} name="category" onChange={handleChange} size="small" label="Type" />
                            </Grid>
                            <Grid item xs={4} md={4} lg={4}>
                                {!isMfgListLoading ? (
                                    <Autocomplete
                                        ref={manufacturerRef}
                                        value={mfgValue || ''}
                                        onBlur={handleMfgBlur}
                                        options={companyMfgData?.data as any
                                        }
                                        onTextFieldChange={handleInputChange}
                                        onChange={handleMfgChange}
                                        placeholder="Type a manufacturer..."
                                        id="mfgName"
                                        label="Manufacturer Name"
                                        optionlabelkeyname="manufacturer_name"
                                        filterOptionsCallBack={filterHandler}
                                        freeSolo
                                    />
                                ) : (
                                    <Skeleton variant="rectangular" width="100%" height={36} sx={{ borderRadius: 1 }} />
                                )}
                            </Grid>
                            <Grid item xs={6} md={6} lg={6}>
                                <Box display="flex" alignItems="center">
                                    <TextField
                                        ref={modelDivRef}
                                        inputRef={modelRef}
                                        onKeyDown={onPressEnter}
                                        value={productData.model_number || ''}
                                        name="model_number"
                                        onChange={handleChange}
                                        size="small"
                                        label="Model number"
                                    />
                                    <Box onClick={handleEditProductInfo} ml={5} sx={{ cursor: 'pointer' }}>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                pointerEvents: 'none',
                                            }}
                                        >
                                            <EditIcon
                                                color={editDetails ? 'primary' : 'secondary'}
                                                fontSize="small"
                                            />
                                        </IconButton>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </>
            )}
            {refreshBodDetail && <RefreshBodModal open={refreshBodDetail} handleDialog={toggleRefreshDataSheet} onAccept={onClickRefreshOption} />}
        </>
    );
};

export default ProductInfo;
